// Capturing the visible page for a feedback report, in the browser.
//
// The page is drawn from its own DOM (`captureDom`): a copy of the body with
// the page's stylesheets, laid out at the viewport's size inside an SVG
// image and drawn onto a canvas. It asks for no permission, works on phones,
// leaves Changeling's own feedback controls out, and knows where every code
// and diff area is, so they can be blurred exactly. What it cannot draw is
// left out on purpose and counted: embedded pages (iframes: brief tabs, other
// origins), images from other sites (avatars, images in comments), which a
// canvas may not read, and video. Where the browser will not let a canvas read
// back such an image at all (Safari taints any SVG holding HTML),
// `captureDisplay` asks the browser to capture the tab instead
// (`getDisplayMedia`), which draws everything but needs the reporter's
// permission and is not offered on phones.
import type { Rect } from './annotate';

export interface Capture {
  /** The page as it looked, `scale` device pixels per CSS pixel. */
  canvas: HTMLCanvasElement;
  scale: number;
  /** The viewport's size, in CSS pixels: annotations and areas are in these. */
  width: number;
  height: number;
  /** Code and diff areas, in CSS pixels of the viewport. */
  codeAreas: Rect[];
  via: 'dom' | 'display';
  /** What the DOM capture left out. */
  omitted: { frames: number; images: number };
}

/** Marks an element (and what it holds) as Changeling's own feedback controls: never captured. */
export const IGNORE_ATTR = 'data-feedback-ignore';

/** Areas holding a pull request's code: diffs, file previews, editors, code blocks. */
export const CODE_SELECTORS = ['.block-lines', '.codepanel', '.file-preview', '.md-compare', '.img-compare', '.cm-editor', 'pre', '.source-cell', '.suggestion-diff'];

/** The most device pixels per CSS pixel a capture is drawn at. */
const MAX_SCALE = 2;

/**
 * The outermost elements of `doc` matching CODE_SELECTORS, as their visible
 * rectangles in the viewport (`width` × `height`); those out of view are left out.
 */
export function codeAreas(doc: Document, width: number, height: number): Rect[] {
  const selector = CODE_SELECTORS.join(',');
  const out: Rect[] = [];
  for (const el of doc.querySelectorAll(selector)) {
    if (el.parentElement?.closest(selector)) continue;
    if (el.closest(`[${IGNORE_ATTR}]`)) continue;
    const clipped = clip(el.getBoundingClientRect(), width, height);
    if (clipped) out.push(clipped);
  }
  return out;
}

/** `r` within the viewport, or null when none of it is in view. */
export function clip(r: { left: number; top: number; right: number; bottom: number }, width: number, height: number): Rect | null {
  const x = Math.max(0, r.left);
  const y = Math.max(0, r.top);
  const w = Math.min(width, r.right) - x;
  const h = Math.min(height, r.bottom) - y;
  return w > 0 && h > 0 ? { x, y, w, h } : null;
}

/** The page's stylesheets as text; a sheet the page may not read (another origin) is left out. */
function styles(doc: Document): string {
  const out: string[] = [];
  for (const sheet of doc.styleSheets) {
    try {
      out.push([...sheet.cssRules].map((r) => r.cssText).join('\n'));
    } catch {
      /* another origin's: its rules cannot be read */
    }
  }
  return out.join('\n');
}

/** The root element's custom properties (the colour tokens), resolved, as a style declaration. */
function rootTokens(doc: Document): string {
  const cs = getComputedStyle(doc.documentElement);
  const out: string[] = [];
  for (let i = 0; i < cs.length; i++) {
    const name = cs[i];
    if (name.startsWith('--')) out.push(`${name}:${cs.getPropertyValue(name)}`);
  }
  return out.join(';');
}

function px(n: number): string {
  return `${Math.round(n * 100) / 100}px`;
}

/** A stand-in for something the capture does not draw: a box of the same size, hatched, with what it was. */
function placeholder(doc: Document, rect: DOMRect, what: string): HTMLElement {
  const d = doc.createElement('div');
  d.textContent = what;
  d.setAttribute(
    'style',
    `display:flex;align-items:center;justify-content:center;width:${px(rect.width)};height:${px(rect.height)};` +
      'background:repeating-linear-gradient(45deg,#8881 0 8px,#8883 8px 16px);color:#888;font:12px sans-serif;overflow:hidden;box-sizing:border-box;border:1px dashed #888',
  );
  return d;
}

function sameOrigin(src: string, origin: string): boolean {
  if (src.startsWith('data:') || src.startsWith('blob:')) return true;
  try {
    return new URL(src, origin).origin === origin;
  } catch {
    return false;
  }
}

/** An image the page shows, as a data URL; null when it may not be read. */
function imageData(img: HTMLImageElement): string | null {
  if (!img.complete || !img.naturalWidth) return null;
  try {
    const c = document.createElement('canvas');
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    c.getContext('2d')!.drawImage(img, 0, 0);
    return c.toDataURL();
  } catch {
    return null;
  }
}

/** The nearest ancestor of `el` that positions an absolutely positioned descendant (or null: the body). */
function containingBlock(el: Element): Element | null {
  for (let a = el.parentElement; a && a !== el.ownerDocument.body; a = a.parentElement) {
    const cs = getComputedStyle(a);
    if (cs.position !== 'static' || cs.transform !== 'none') return a;
  }
  return null;
}

/**
 * Makes `copy` (a deep copy of `orig`) draw as `orig` looks now: scroll
 * positions, stuck and fixed elements, typed values, images, canvases and
 * embedded pages. Adds the copies to remove (Changeling's feedback
 * controls, scripts, closed dialogs) to `drop`.
 */
function freeze(orig: Element, copy: Element, origin: string, omitted: Capture['omitted'], drop: Element[]): void {
  const doc = orig.ownerDocument;
  // Changeling's own feedback controls, what never draws, and closed dialogs.
  const closedDialog = orig instanceof HTMLDialogElement && !orig.open;
  if (orig.hasAttribute(IGNORE_ATTR) || closedDialog || ['SCRIPT', 'NOSCRIPT', 'TEMPLATE'].includes(orig.tagName)) {
    drop.push(copy);
    return;
  }
  if (!(copy instanceof HTMLElement || copy instanceof SVGElement)) return;
  const style = copy.style;
  const cs = getComputedStyle(orig);
  if (cs.position === 'sticky') {
    const stuck = orig.getBoundingClientRect();
    const o = orig as HTMLElement;
    const prior = o.style.getPropertyValue('position');
    const priority = o.style.getPropertyPriority('position');
    o.style.setProperty('position', 'static', 'important');
    const free = orig.getBoundingClientRect();
    o.style.setProperty('position', prior, priority);
    style.setProperty('position', 'relative', 'important');
    style.setProperty('top', px(stuck.top - free.top), 'important');
    style.setProperty('left', px(stuck.left - free.left), 'important');
    style.setProperty('bottom', 'auto', 'important');
    style.setProperty('right', 'auto', 'important');
  } else if (cs.position === 'fixed') {
    const r = orig.getBoundingClientRect();
    const block = containingBlock(orig) ?? doc.body;
    const b = block.getBoundingClientRect();
    style.setProperty('position', 'absolute', 'important');
    style.setProperty('top', px(r.top - b.top - block.clientTop), 'important');
    style.setProperty('left', px(r.left - b.left - block.clientLeft), 'important');
    style.setProperty('width', px(r.width), 'important');
    style.setProperty('height', px(r.height), 'important');
    style.setProperty('bottom', 'auto', 'important');
    style.setProperty('right', 'auto', 'important');
    style.setProperty('margin', '0', 'important');
  }
  if ((orig.scrollTop || orig.scrollLeft) && orig !== doc.body) {
    for (const child of copy.children) {
      if (child instanceof HTMLElement || child instanceof SVGElement)
        child.style.setProperty('translate', `${px(-orig.scrollLeft)} ${px(-orig.scrollTop)}`);
    }
  }
  // What is typed into fields (unsent comments, searches) is never copied:
  // see `emptyFields`. Choices are.
  if (orig instanceof HTMLInputElement) {
    if (orig.type === 'checkbox' || orig.type === 'radio') {
      if (orig.checked) copy.setAttribute('checked', '');
      else copy.removeAttribute('checked');
    }
  } else if (orig instanceof HTMLSelectElement) {
    [...copy.querySelectorAll('option')].forEach((o, i) => (i === orig.selectedIndex ? o.setAttribute('selected', '') : o.removeAttribute('selected')));
  } else if (orig instanceof HTMLImageElement) {
    const src = orig.currentSrc || orig.src;
    const data = src && sameOrigin(src, origin) ? imageData(orig) : null;
    if (data) {
      copy.setAttribute('src', data);
      copy.removeAttribute('srcset');
      copy.removeAttribute('loading');
    } else if (src) {
      omitted.images++;
      copy.replaceWith(placeholder(doc, orig.getBoundingClientRect(), ''));
    }
  } else if (orig instanceof HTMLCanvasElement) {
    let data: string | null = null;
    try {
      data = orig.toDataURL();
    } catch {
      /* tainted */
    }
    const img = doc.createElement('img');
    if (data) img.src = data;
    img.setAttribute('style', copy.getAttribute('style') ?? '');
    img.className = copy.className;
    img.width = orig.clientWidth;
    img.height = orig.clientHeight;
    copy.replaceWith(img);
  } else if (['IFRAME', 'VIDEO', 'EMBED', 'OBJECT'].includes(orig.tagName)) {
    omitted.frames++;
    copy.replaceWith(placeholder(doc, orig.getBoundingClientRect(), orig.tagName === 'VIDEO' ? 'Video not captured' : 'Embedded page not captured'));
  }
}

/**
 * Empties every text field in `copy` (a copy of the page): text inputs
 * lose their value, text areas their text. A screenshot shows the fields,
 * never what is typed in them, such as an unsent comment.
 */
export function emptyFields(copy: Element): void {
  for (const f of copy.querySelectorAll('input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit])')) f.removeAttribute('value');
  for (const t of copy.querySelectorAll('textarea')) t.textContent = '';
  for (const e of copy.querySelectorAll('[contenteditable]:not([contenteditable="false"])')) e.textContent = '';
}

/** Walks `orig` and `copy` (its deep copy) in step, freezing each element. */
function freezeTree(orig: Element, copy: Element, origin: string, omitted: Capture['omitted'], drop: Element[], root = true): void {
  freeze(orig, copy, origin, omitted, drop);
  // Dropped, or replaced by a stand-in: nothing below it is drawn.
  if (drop.includes(copy) || (!root && copy.parentNode === null)) return;
  const o = orig.children;
  const c = copy.children;
  if (o.length !== c.length) return;
  for (let i = 0; i < o.length; i++) freezeTree(o[i], c[i], origin, omitted, drop, false);
}

/** The page drawn from its DOM. Rejects when the browser will not let a canvas read it back. */
export async function captureDom(win: Window = window): Promise<Capture> {
  const doc = win.document;
  const width = doc.documentElement.clientWidth || win.innerWidth;
  const height = win.innerHeight;
  const scale = Math.min(MAX_SCALE, win.devicePixelRatio || 1);
  const omitted = { frames: 0, images: 0 };
  const areas = codeAreas(doc, width, height);

  const body = doc.body.cloneNode(true) as HTMLElement;
  const drop: Element[] = [];
  freezeTree(doc.body, body, win.location.origin, omitted, drop);
  for (const d of drop) d.remove();
  emptyFields(body);
  const bodyStyle = getComputedStyle(doc.body);
  body.style.setProperty('position', 'relative', 'important');
  body.style.setProperty('width', px(width), 'important');
  body.style.setProperty('translate', `${px(-win.scrollX)} ${px(-win.scrollY)}`);

  const rootAttrs = [...doc.documentElement.attributes]
    .filter((a) => a.name === 'data-theme' || a.name === 'class' || a.name === 'lang')
    .map((a) => ` ${a.name}="${escapeAttr(a.value)}"`)
    .join('');
  const background = bodyStyle.backgroundColor;
  const css = styles(doc).replaceAll(']]>', ']] >');
  const html = new XMLSerializer().serializeToString(body);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"${rootAttrs}>` +
    `<foreignObject x="0" y="0" width="100%" height="100%">` +
    `<div xmlns="http://www.w3.org/1999/xhtml" style="${escapeAttr(rootTokens(doc))};width:${width}px;height:${height}px;overflow:hidden;background:${background}">` +
    `<style><![CDATA[${css}]]></style>${html}</div></foreignObject></svg>`;

  const img = new Image();
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  await img.decode();
  const canvas = doc.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  // Throws (SecurityError) where the browser taints a canvas drawn from HTML.
  ctx.getImageData(0, 0, 1, 1);
  return { canvas, scale, width, height, codeAreas: areas, via: 'dom', omitted };
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/** Whether this browser can capture a tab (`getDisplayMedia`); phones cannot. */
export function canCaptureDisplay(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
}

/** A nonce naming this page to a capture of its own tab (Chrome's capture handles). */
const HANDLE = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Math.random());

/**
 * Whether a display capture shows this tab, so the code areas measured
 * here land on the right pixels: a browser tab (not a window or a screen)
 * that carries this page's capture handle. A tab with no handle of ours
 * (`null`, or another's) is another page. Where the browser has no capture
 * handles (`handle` undefined) nothing proves which tab it is (another tab
 * of the same window has the same shape), so the capture is refused and
 * the page is drawn from its DOM.
 */
export function capturedThisTab(settings: { displaySurface?: string }, handle: { handle?: string } | null | undefined, nonce: string): boolean {
  return settings.displaySurface === 'browser' && handle != null && handle.handle === nonce;
}

/** A dialog, as `steppingAside` needs it. */
export interface Hideable {
  readonly open: boolean;
  close(): void;
  showModal(): void;
}

const twoFrames = () => new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

/**
 * Runs `capture` with `dialog` (the feedback dialog) closed and off the
 * screen (`painted`: two frames, so the page has been drawn without it),
 * and shows it again whatever comes of the capture. A capture of the
 * screen never holds the dialog; one that could is not started.
 */
export async function steppingAside<T>(dialog: Hideable, capture: () => Promise<T>, painted: () => Promise<void> = twoFrames): Promise<T> {
  dialog.close();
  try {
    await painted();
    if (dialog.open) throw new Error('The feedback dialog could not be hidden for the capture.');
    return await capture();
  } finally {
    if (!dialog.open) dialog.showModal();
  }
}

/** A display capture that was not this tab: discarded. */
export class NotThisTab extends Error {
  constructor() {
    super('The browser’s capture could not be confirmed to be this tab, so it was discarded.');
  }
}

/**
 * This tab, captured by the browser after the reporter allows it. Only the
 * current tab is asked for (`preferCurrentTab`, `selfBrowserSurface:
 * 'include'`, no switching), and the result is checked
 * (`capturedThisTab`): anything else is discarded with `NotThisTab`.
 * The caller hides the feedback dialog first (`steppingAside`).
 */
export async function captureDisplay(win: Window = window): Promise<Capture> {
  const doc = win.document;
  const width = doc.documentElement.clientWidth || win.innerWidth;
  const height = win.innerHeight;
  const areas = codeAreas(doc, width, height);
  const media = navigator.mediaDevices as MediaDevices & { setCaptureHandleConfig?: (c: { handle: string; permittedOrigins: string[] }) => void };
  try {
    media.setCaptureHandleConfig?.({ handle: HANDLE, permittedOrigins: [win.location.origin] });
  } catch {
    /* not this browser's: the frame's shape is checked instead */
  }
  const stream = await media.getDisplayMedia({
    video: { displaySurface: 'browser' },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
    surfaceSwitching: 'exclude',
    monitorTypeSurfaces: 'exclude',
  } as DisplayMediaStreamOptions);
  try {
    const track = stream.getVideoTracks()[0] as MediaStreamTrack & { getCaptureHandle?: () => { handle?: string } | null };
    const video = doc.createElement('video');
    video.muted = true;
    video.srcObject = stream;
    await video.play();
    await new Promise((r) => setTimeout(r, 200));
    const frame = { width: video.videoWidth, height: video.videoHeight };
    const handle = typeof track?.getCaptureHandle === 'function' ? track.getCaptureHandle() : undefined;
    if (!track || !capturedThisTab(track.getSettings(), handle, HANDLE)) throw new NotThisTab();
    const canvas = doc.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    return { canvas, scale: frame.width / width, width, height, codeAreas: areas, via: 'display', omitted: { frames: 0, images: 0 } };
  } finally {
    for (const t of stream.getTracks()) t.stop();
  }
}

/** Blurs `areas` (CSS pixels) of `ctx`'s canvas, drawn at `scale`, past reading: each is shrunk to a few pixels and stretched back. */
export function blurAreas(ctx: CanvasRenderingContext2D, areas: readonly Rect[], scale: number): void {
  const factor = 12;
  for (const a of areas) {
    const x = Math.round(a.x * scale);
    const y = Math.round(a.y * scale);
    const w = Math.round(a.w * scale);
    const h = Math.round(a.h * scale);
    if (w <= 0 || h <= 0) continue;
    const small = document.createElement('canvas');
    small.width = Math.max(1, Math.round(w / factor));
    small.height = Math.max(1, Math.round(h / factor));
    const s = small.getContext('2d')!;
    s.imageSmoothingEnabled = true;
    s.drawImage(ctx.canvas, x, y, w, h, 0, 0, small.width, small.height);
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(small, 0, 0, small.width, small.height, x, y, w, h);
    ctx.restore();
  }
}

/** The largest screenshot the server takes, in bytes. */
export const MAX_SCREENSHOT = 5 * 1024 * 1024;

/**
 * `canvas` as base64 image data within MAX_SCREENSHOT: a PNG when it fits
 * (sharp for text), else a JPEG, smaller each try until it fits.
 */
export async function encode(canvas: HTMLCanvasElement): Promise<{ base64: string; type: string; bytes: number }> {
  const blob = (c: HTMLCanvasElement, type: string, quality?: number) =>
    new Promise<Blob>((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('The screenshot could not be encoded.'))), type, quality));
  let out = await blob(canvas, 'image/png');
  let source = canvas;
  for (let shrink = 1; out.size > MAX_SCREENSHOT * 0.98 && shrink > 0.2; shrink *= 0.75) {
    if (shrink < 1) {
      const c = document.createElement('canvas');
      c.width = Math.round(canvas.width * shrink);
      c.height = Math.round(canvas.height * shrink);
      c.getContext('2d')!.drawImage(canvas, 0, 0, c.width, c.height);
      source = c;
    }
    out = await blob(source, 'image/jpeg', 0.85);
  }
  const buf = new Uint8Array(await out.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return { base64: btoa(bin), type: out.type, bytes: out.size };
}
